/**
 * Sample portfolios for "My portfolios" — built by the real optimiser and
 * marked to real prices, but saved nowhere and traded by no one.
 *
 * Saving a portfolio and paper-trading it nightly needs a backend write path
 * that does not exist yet (B21), so until then the shelf shows what saved
 * books WOULD look like: each recipe below is run through
 * `alphaOptimiseDryRun` on the latest ranking, bought at the first close on or
 * after that ranking's date, and held to the latest close. No hindsight: the
 * picks are made on the ranking date and only later prices are used.
 *
 * Paper vs twin follows the platform's paper convention: the paper book pays
 * the cost model on entry (5 bp commission + 12 bp taxes), the simulated twin
 * trades frictionlessly — so "vs twin" is the execution drag.
 *
 * Pure functions, no React.
 */

/** Nifty 50 ETF — the benchmark every sample is marked against. */
export const SAMPLE_BENCHMARK = { symbol: 'NIFTYBEES.NSE', label: 'Nifty 50 (NIFTYBEES)' };

/** Notional capital each sample starts with, in rupees (₹1 crore). */
export const SAMPLE_CAPITAL_INR = 1e7;

/** Entry cost the paper book pays and the twin does not: commission 5 bp + taxes 12 bp. */
export const SAMPLE_COST_BPS = 17;

/**
 * The recipes: a universe cut of the month ranking plus the optimiser's caps,
 * in the units the Exposure optimiser screen uses (percent).
 */
export const SAMPLE_RECIPES = [
  {
    id: 'top-20-concentrated',
    name: 'Top-20 concentrated',
    owner: 'sample',
    policy: 'every 21 sessions',
    criteria: { horizon: 'month', topN: 30, nNames: 20, sectorCap: 25, singleStockCap: 8, minPosition: 2 },
  },
  {
    id: 'high-conviction-10',
    name: 'High-conviction 10',
    owner: 'sample',
    policy: 'follow the model',
    criteria: { horizon: 'month', topN: 15, nNames: 10, sectorCap: 30, singleStockCap: 12, minPosition: 5 },
  },
  {
    id: 'broad-30',
    name: 'Broad 30, low concentration',
    owner: 'sample',
    policy: 'every 63 sessions',
    criteria: { horizon: 'month', topN: 60, nNames: 30, sectorCap: 20, singleStockCap: 5, minPosition: 2 },
  },
  {
    id: 'diversified-40',
    name: 'Diversified 40',
    owner: 'sample',
    policy: 'manual',
    criteria: { horizon: 'month', topN: 120, nNames: 40, sectorCap: 20, singleStockCap: 4, minPosition: 1 },
  },
];

/** A recipe as `alphaOptimiseDryRun` variables (fractions). */
export function recipeInput(recipe) {
  const c = recipe.criteria;
  return {
    horizon: c.horizon,
    topN: c.topN,
    nNames: c.nNames,
    sectorCap: c.sectorCap / 100,
    singleStockCap: c.singleStockCap / 100,
    minPosition: c.minPosition / 100,
  };
}

/** "Universe · criteria" as the shelf's table shows it. */
export const recipeSummary = (r) => `top ${r.criteria.topN} ${r.criteria.horizon} rank · max alpha / σ² · ${r.criteria.nNames} names`;

/** The constraint line on "Saved criteria". */
export const recipeConstraints = (r) => {
  const c = r.criteria;
  return `name ≤ ${c.singleStockCap}% · sector ≤ ${c.sectorCap}% · min position ${c.minPosition}%`;
};

/** The id a saved book would carry: `custom:<owner>:<slug>`. */
export const recipePortfolioId = (r) => `custom:${r.owner}:${r.id}`;

const priceOf = (p) => Number(p.adjustedClose ?? p.close);
const day = (d) => String(d).slice(0, 10);

/** symbol → sorted [{ date, price }], finite prices only. */
function cleanSeries(history) {
  const out = {};
  Object.entries(history ?? {}).forEach(([symbol, points]) => {
    out[symbol] = (points ?? [])
      .map((p) => ({ date: day(p.date), price: priceOf(p) }))
      .filter((p) => Number.isFinite(p.price) && p.price > 0)
      .sort((a, b) => a.date.localeCompare(b.date));
  });
  return out;
}

/** The last price on or before `date` (carried forward across a missing day). */
function priceAt(series, date) {
  let found = null;
  for (let i = 0; i < series.length && series[i].date <= date; i += 1) found = series[i].price;
  return found;
}

/**
 * Buy-and-hold from `startDate` with the optimiser's weights.
 *
 * Entry is each name's first close ON OR AFTER the ranking date. A name with
 * no price after the start is dropped and the rest re-scaled to 100%; the drop
 * is reported in `missing`.
 *
 * Returns, rebased to 1.0:
 *   nav    the paper book, after the entry cost
 *   twin   the frictionless twin (same holdings, no cost)
 *   bench  the benchmark
 *   positions [{ symbol, target, actual, drift, sinceEntry, entryDate, entryPrice, lastPrice, quantity }]
 *   fills  the entry trades on each name's entry date
 *   missing, metrics
 */
export function markToMarket(holdings, history, startDate, {
  benchmarkSymbol = SAMPLE_BENCHMARK.symbol, costBps = SAMPLE_COST_BPS, capital = SAMPLE_CAPITAL_INR,
} = {}) {
  const series = cleanSeries(history);
  const start = day(startDate);
  const keep = 1 - costBps / 1e4;

  const entries = [];
  const missing = [];
  holdings.forEach((h) => {
    const s = series[h.securitySymbol] ?? [];
    const first = s.find((p) => p.date >= start);
    if (first) entries.push({ symbol: h.securitySymbol, target: Number(h.weight), entryDate: first.date, entryPrice: first.price, series: s });
    else missing.push(h.securitySymbol);
  });
  const total = entries.reduce((sum, e) => sum + e.target, 0) || 1;
  entries.forEach((e) => { e.w0 = e.target / total; });

  const benchSeries = series[benchmarkSymbol] ?? [];
  const dates = [...new Set([
    ...entries.flatMap((e) => e.series.map((p) => p.date)),
    ...benchSeries.map((p) => p.date),
  ])].filter((d) => d >= start).sort();

  const twin = [];
  dates.forEach((d) => {
    let value = 0;
    let priced = true;
    entries.forEach((e) => {
      const p = d >= e.entryDate ? priceAt(e.series, d) : e.entryPrice;
      if (p == null) priced = false;
      else value += e.w0 * (p / e.entryPrice);
    });
    if (priced && entries.length) twin.push({ date: d, value });
  });
  const nav = twin.map((p) => ({ date: p.date, value: p.value * keep }));

  const benchEntry = benchSeries.find((p) => p.date >= start);
  const bench = benchEntry
    ? twin.map((p) => ({ date: p.date, value: (priceAt(benchSeries, p.date) ?? benchEntry.price) / benchEntry.price }))
    : [];

  const last = twin[twin.length - 1]?.date ?? start;
  const positions = entries.map((e) => {
    const lastPrice = priceAt(e.series, last) ?? e.entryPrice;
    const grown = e.w0 * (lastPrice / e.entryPrice);
    const quantity = Math.floor((capital * keep * e.w0) / e.entryPrice);
    return { symbol: e.symbol, target: e.w0, grown, sinceEntry: lastPrice / e.entryPrice - 1, entryDate: e.entryDate, entryPrice: e.entryPrice, lastPrice, quantity };
  });
  const grownTotal = positions.reduce((s, p) => s + p.grown, 0) || 1;
  positions.forEach((p) => { p.actual = p.grown / grownTotal; p.drift = p.actual - p.target; });

  const fills = positions.map((p) => ({
    tradeDate: p.entryDate,
    securitySymbol: p.symbol,
    side: 'buy',
    quantity: p.quantity,
    price: p.entryPrice,
    notional: p.quantity * p.entryPrice,
    costBps,
  }));

  return { start, end: last, nav, twin, bench, positions, fills, missing, metrics: metricsOf(nav, twin, bench, positions, costBps) };
}

/** Return, benchmark, twin, last session, drawdown, realised TE, drift, and the return split. */
function metricsOf(nav, twin, bench, positions, costBps) {
  if (nav.length < 1) return null;
  const end = nav[nav.length - 1].value;
  const twinEnd = twin[twin.length - 1].value;
  const benchByDate = new Map(bench.map((b) => [b.date, b.value]));
  const benchEnd = benchByDate.get(nav[nav.length - 1].date) ?? null;

  let peak = -Infinity;
  let maxDd = 0;
  nav.forEach((p) => {
    peak = Math.max(peak, p.value);
    maxDd = Math.min(maxDd, p.value / peak - 1);
  });

  const active = [];
  for (let i = 1; i < nav.length; i += 1) {
    const b0 = benchByDate.get(nav[i - 1].date);
    const b1 = benchByDate.get(nav[i].date);
    if (b0 && b1) active.push((nav[i].value / nav[i - 1].value - 1) - (b1 / b0 - 1));
  }
  const mean = active.reduce((s, x) => s + x, 0) / (active.length || 1);
  const te = active.length > 1
    ? Math.sqrt(active.reduce((s, x) => s + (x - mean) ** 2, 0) / (active.length - 1)) * Math.sqrt(252)
    : null;

  const benchReturn = benchEnd == null ? null : benchEnd - 1;
  return {
    nav: end,
    sinceStart: end - 1,
    benchmark: benchReturn,
    vsBenchmark: benchReturn == null ? null : end - benchEnd,
    vsTwin: end - twinEnd,
    lastSession: nav.length > 1 ? end / nav[nav.length - 2].value - 1 : null,
    maxDrawdown: maxDd,
    trackingError: te,
    drift: positions.reduce((s, p) => s + Math.abs(p.drift), 0) / 2,
    costBps,
    sessions: nav.length,
    /* Where the return came from, as return fractions that add up to `sinceStart`. */
    attribution: benchReturn == null ? null : {
      benchmark: benchReturn,
      selection: twinEnd - benchEnd,
      costs: end - twinEnd,
    },
  };
}
